<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStoreGetters, useMapGetter, useStore } from 'dashboard/composables/store';
import { useAlert, useTrack } from 'dashboard/composables';
import { CAMPAIGN_TYPES } from 'shared/constants/campaign.js';
import { CAMPAIGNS_EVENTS } from 'dashboard/helper/AnalyticsHelper/events.js';

import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import CampaignLayout from 'dashboard/components-next/Campaigns/CampaignLayout.vue';
import CampaignList from 'dashboard/components-next/Campaigns/Pages/CampaignPage/CampaignList.vue';
import DisparosCampaignForm from 'dashboard/components-next/Campaigns/Pages/CampaignPage/DisparosCampaign/DisparosCampaignForm.vue';
import ConfirmDeleteCampaignDialog from 'dashboard/components-next/Campaigns/Pages/CampaignPage/ConfirmDeleteCampaignDialog.vue';
import DisparosCampaignDetailsDialog from 'dashboard/components-next/Campaigns/CampaignCard/DisparosCampaignDetailsDialog.vue';
import DisparosCampaignEmptyState from 'dashboard/components-next/Campaigns/EmptyState/DisparosCampaignEmptyState.vue';

const { t } = useI18n();
const getters = useStoreGetters();
const store = useStore();

const selectedCampaign = ref(null);
const showForm = ref(false);
const formMode = ref('create');

const uiFlags = useMapGetter('campaigns/getUIFlags');
const isFetchingCampaigns = computed(() => uiFlags.value.isFetching);

const confirmDeleteCampaignDialogRef = ref(null);
const detailsDialogRef = ref(null);
const selectedCampaignForDetails = ref(null);

const DisparosCampaigns = computed(() => getters['campaigns/getDisparosCampaigns'].value);

const hasNoDisparosCampaigns = computed(
  () => DisparosCampaigns.value?.length === 0 && !isFetchingCampaigns.value
);

const headerTitle = computed(() =>
  showForm.value ? t('CAMPAIGN.DISPARO.CREATE.TITLE') : t('CAMPAIGN.DISPARO.HEADER_TITLE')
);

const headerButtonLabel = computed(() =>
  showForm.value ? '' : t('CAMPAIGN.DISPARO.NEW_CAMPAIGN')
);

const handleNewCampaign = () => {
  formMode.value = 'create';
  selectedCampaign.value = null;
  showForm.value = true;
};

const handleBack = () => {
  showForm.value = false;
  selectedCampaign.value = null;
};

const handleFormSubmit = async campaignDetails => {
  try {
    await store.dispatch('campaigns/create', campaignDetails);

    useTrack(CAMPAIGNS_EVENTS.CREATE_CAMPAIGN, {
      type: CAMPAIGN_TYPES.ONE_OFF,
    });

    useAlert(t('CAMPAIGN.DISPARO.CREATE.FORM.API.SUCCESS_MESSAGE'));
  } catch (error) {
    const errorMessage =
      error?.response?.message ||
      t('CAMPAIGN.DISPARO.CREATE.FORM.API.ERROR_MESSAGE');
    useAlert(errorMessage);
    return; // Don't close form on error
  }

  showForm.value = false;
  selectedCampaign.value = null;
  store.dispatch('campaigns/get');
};

const handleDuplicate = campaign => {
  selectedCampaign.value = campaign;
  formMode.value = 'duplicate';
  showForm.value = true;
};

const handleDelete = campaign => {
  selectedCampaign.value = campaign;
  confirmDeleteCampaignDialogRef.value.dialogRef.open();
};

const handleViewDetails = campaign => {
  selectedCampaignForDetails.value = campaign;
  detailsDialogRef.value?.open();
};
</script>

<template>
  <CampaignLayout
    :header-title="headerTitle"
    :button-label="headerButtonLabel"
    @click="showForm ? handleBack() : handleNewCampaign()"
  >
    <div
      v-if="isFetchingCampaigns && !showForm"
      class="flex items-center justify-center py-10 text-n-slate-11"
    >
      <Spinner />
    </div>

    <!-- Full-page form -->
    <DisparosCampaignForm
      v-if="showForm"
      :mode="formMode"
      :selected-campaign="selectedCampaign"
      @submit="handleFormSubmit"
      @cancel="handleBack"
    />

    <!-- Campaign list -->
    <CampaignList
      v-else-if="!hasNoDisparosCampaigns"
      :campaigns="DisparosCampaigns"
      is-disparos-type
      @delete="handleDelete"
      @duplicate="handleDuplicate"
      @view-details="handleViewDetails"
    />
    <DisparosCampaignEmptyState
      v-else
      :title="t('CAMPAIGN.DISPARO.EMPTY_STATE.TITLE')"
      :subtitle="t('CAMPAIGN.DISPARO.EMPTY_STATE.SUBTITLE')"
      class="pt-14"
    />
    <ConfirmDeleteCampaignDialog
      ref="confirmDeleteCampaignDialogRef"
      :selected-campaign="selectedCampaign"
    />
    <DisparosCampaignDetailsDialog
      ref="detailsDialogRef"
      :campaign="selectedCampaignForDetails"
    />
  </CampaignLayout>
</template>
